import React, { useState, useEffect, useRef } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, CheckCircle2, FileText, RotateCcw } from "lucide-react";

export const PDF_DESIGNS = [
  { id: "clasico", name: "Clásico", color: "#112240" },
  { id: "barra", name: "Barra superior", color: "#1E3A8A" },
  { id: "banner", name: "Encabezado sólido", color: "#312E81" },
  { id: "centrado", name: "Centrado", color: "#0F766E" },
  { id: "franja", name: "Franja lateral", color: "#7F1D1D" },
  { id: "minimal", name: "Minimalista", color: "#334155" },
  { id: "marco", name: "Marco", color: "#065F46" },
  { id: "linea", name: "Doble línea", color: "#B45309" },
  { id: "esquina", name: "Bloque lateral", color: "#6D28D9" },
  { id: "elegante", name: "Elegante", color: "#0C4A6E" },
];

const SWATCHES = ["#112240", "#1E3A8A", "#0F766E", "#7F1D1D", "#B45309", "#6D28D9", "#065F46", "#0C4A6E", "#334155", "#BE185D"];

// Miniature preview of a PDF header for a given design
function DesignPreview({ design, color, heading, logoUrl, scale = 1, mini = false }) {
  const logo = logoUrl ? (
    <img src={logoUrl} alt="logo" style={{ height: `${(mini ? 16 : 34) * scale}px`, width: "auto", maxWidth: "45%", objectFit: "contain" }} />
  ) : null;
  const title = (extra = {}) => (
    <div style={{ color, fontWeight: 800, fontSize: mini ? 11 : 20, lineHeight: 1.1, ...extra }}>{heading || "ZAVIA"}</div>
  );
  const sub = <div style={{ color, fontWeight: 700, fontSize: mini ? 7 : 11, marginTop: 2 }}>Reporte de asistencia</div>;
  const wrap = { padding: mini ? 8 : 16, background: "#fff", borderRadius: 6, textAlign: "left", overflow: "hidden" };

  if (design === "barra" || design === "banner") {
    return (
      <div style={wrap}>
        {logo && <div style={{ marginBottom: 4 }}>{logo}</div>}
        <div style={{ background: color, color: "#fff", fontWeight: 800, fontSize: mini ? 11 : 20, textAlign: "center", padding: mini ? "6px" : `${design === "banner" ? 14 : 10}px`, borderRadius: 3 }}>{heading || "ZAVIA"}</div>
        {!mini && sub}
      </div>
    );
  }
  if (design === "centrado") {
    return (
      <div style={{ ...wrap, textAlign: "center" }}>
        {logo && <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>{logo}</div>}
        {title({ textAlign: "center" })}
        <div style={{ color, fontWeight: 700, fontSize: mini ? 7 : 11, marginTop: 2 }}>Reporte de asistencia</div>
      </div>
    );
  }
  if (design === "franja") {
    return (
      <div style={wrap}>
        {logo && <div style={{ marginBottom: 4 }}>{logo}</div>}
        <div style={{ display: "flex", alignItems: "stretch", gap: mini ? 6 : 10 }}>
          <div style={{ width: mini ? 4 : 6, background: color, borderRadius: 2 }} />
          <div>{title()}{!mini && sub}</div>
        </div>
      </div>
    );
  }
  if (design === "minimal") {
    return (
      <div style={wrap}>
        {logo && <div style={{ marginBottom: 4 }}>{logo}</div>}
        {title()}
        <div style={{ height: 2, background: color, margin: "4px 0" }} />
        {!mini && sub}
      </div>
    );
  }
  if (design === "marco") {
    return (
      <div style={wrap}>
        <div style={{ border: `2px solid ${color}`, borderRadius: 4, padding: mini ? 6 : 12 }}>
          {logo && <div style={{ marginBottom: 4 }}>{logo}</div>}
          {title()}{!mini && sub}
        </div>
      </div>
    );
  }
  if (design === "linea") {
    return (
      <div style={wrap}>
        <div style={{ height: 3, background: color, marginBottom: 4 }} />
        {logo && <div style={{ marginBottom: 4 }}>{logo}</div>}
        {title()}
        <div style={{ height: 1.5, background: color, margin: "4px 0" }} />
        {!mini && sub}
      </div>
    );
  }
  if (design === "esquina") {
    return (
      <div style={wrap}>
        {logo && <div style={{ marginBottom: 4 }}>{logo}</div>}
        <div style={{ display: "flex", alignItems: "center", gap: mini ? 6 : 10 }}>
          <div style={{ width: mini ? 16 : 26, height: mini ? 16 : 26, background: color, borderRadius: 3, flexShrink: 0 }} />
          {title()}
        </div>
        {!mini && sub}
      </div>
    );
  }
  if (design === "elegante") {
    return (
      <div style={wrap}>
        {logo && <div style={{ marginBottom: 4 }}>{logo}</div>}
        {title({ fontSize: mini ? 12 : 24 })}
        <div style={{ height: 3, width: "45%", background: color, margin: "3px 0 6px" }} />
        {!mini && <div style={{ color, fontStyle: "italic", fontSize: 11 }}>Reporte de asistencia</div>}
      </div>
    );
  }
  // clasico
  return (
    <div style={wrap}>
      {logo && <div style={{ marginBottom: 4 }}>{logo}</div>}
      {title()}{!mini && sub}
    </div>
  );
}

export function PdfFormatSettings({ user, checkAuth }) {
  const initial = user?.pdf_brand || {};
  const [design, setDesign] = useState(initial.design || "clasico");
  const [color, setColor] = useState(initial.color || "#112240");
  const [heading, setHeading] = useState(initial.heading || user?.school_name || "");
  const [logoScale, setLogoScale] = useState(initial.logo_scale || 1.0);
  const [footer, setFooter] = useState(initial.footer || "");
  const [showSignature, setShowSignature] = useState(!!initial.show_signature);
  const [signatureLabel, setSignatureLabel] = useState(initial.signature_label || "Dirección");
  const [watermark, setWatermark] = useState(!!initial.watermark);
  const [planeacionSubtitle, setPlaneacionSubtitle] = useState(initial.planeacion_subtitle || "");
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef(null);
  const [sigUrl, setSigUrl] = useState(null);
  const [stampUrl, setStampUrl] = useState(null);
  const [assetUploading, setAssetUploading] = useState("");
  const sigRef = useRef(null);
  const stampRef = useRef(null);

  useEffect(() => {
    api.get("/auth/pdf-logo").then((r) => setLogoUrl(r.data.logo_url)).catch(() => {});
    api.get("/auth/pdf-asset/signature").then((r) => setSigUrl(r.data.asset_url)).catch(() => {});
    api.get("/auth/pdf-asset/stamp").then((r) => setStampUrl(r.data.asset_url)).catch(() => {});
  }, []);

  const previewLogo = logoUrl ? `${API}${logoUrl}` : null;
  const previewSig = sigUrl ? `${API}${sigUrl}` : null;
  const previewStamp = stampUrl ? `${API}${stampUrl}` : null;

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/pdf-logo", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setLogoUrl(data.logo_url);
      if (logoRef.current) logoRef.current.value = "";
      toast.success("Logo de PDF actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el logo");
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    try { await api.delete("/auth/pdf-logo"); setLogoUrl(null); toast.success("Logo de PDF eliminado"); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const uploadAsset = async (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAssetUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/auth/pdf-asset/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      if (kind === "signature") { setSigUrl(data.asset_url); if (sigRef.current) sigRef.current.value = ""; }
      else { setStampUrl(data.asset_url); if (stampRef.current) stampRef.current.value = ""; }
      toast.success(kind === "signature" ? "Firma actualizada" : "Sello actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir la imagen");
    } finally { setAssetUploading(""); }
  };

  const removeAsset = async (kind) => {
    try {
      await api.delete(`/auth/pdf-asset/${kind}`);
      if (kind === "signature") setSigUrl(null); else setStampUrl(null);
      toast.success("Imagen eliminada");
    } catch { toast.error("No se pudo eliminar"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", { pdf_brand: {
        design, color, heading: heading.trim(), logo_scale: Number(logoScale),
        footer: footer.trim(), show_signature: showSignature, signature_label: signatureLabel.trim(), watermark,
        planeacion_subtitle: planeacionSubtitle.trim(),
      } });
      await checkAuth?.();
      toast.success("Formato de PDF guardado. Se aplicará a boletas, recibos, asistencias y demás documentos.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const resetStock = async () => {
    setDesign("clasico"); setColor("#112240"); setHeading("ZAVIA"); setLogoScale(1.0);
    setFooter(""); setShowSignature(false); setSignatureLabel("Dirección"); setWatermark(false); setPlaneacionSubtitle("");
    setSaving(true);
    try {
      await api.put("/auth/profile", { pdf_brand: { design: "clasico", color: "#112240", heading: "", logo_scale: 1.0, footer: "", show_signature: false, signature_label: "Dirección", watermark: false, planeacion_subtitle: "" } });
      await checkAuth?.();
      toast.success("Formato restablecido al stock ZAVIA");
    } catch { toast.error("No se pudo restablecer"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="pdf-format-section">
      {/* Save bar */}
      <div className="sticky top-2 z-20 flex items-center justify-between gap-3 bg-navy text-white rounded-md px-4 py-3 shadow-lg" data-testid="pdf-save-bar">
        <div className="min-w-0">
          <div className="font-semibold text-sm">Formato de tus documentos PDF</div>
          <div className="text-white/70 text-[11px]">Se aplica a boletas, asistencias, recibos, pagos, adeudos, credenciales e incidencias.</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={resetStock} disabled={saving} data-testid="pdf-reset-btn"
            className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors">
            <RotateCcw className="w-4 h-4" /> Stock
          </button>
          <button type="button" onClick={save} disabled={saving} data-testid="pdf-save-btn"
            className="inline-flex items-center gap-1.5 bg-white text-navy px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Guardar formato
          </button>
        </div>
      </div>

      {/* Live preview */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8">
        <h2 className="font-display font-semibold text-xl text-navy flex items-center gap-2"><FileText className="w-5 h-5" /> Vista previa</h2>
        <p className="text-sm text-nardo-500 mb-4">Así se verá el encabezado de tus PDF con la configuración actual.</p>
        <div className="border border-nardo-200 rounded-lg overflow-hidden shadow-inner bg-nardo-50 p-4" data-testid="pdf-preview">
          <div className="max-w-lg mx-auto shadow-md">
            <DesignPreview design={design} color={color} heading={heading || user?.school_name || "ZAVIA"} logoUrl={previewLogo} scale={logoScale} />
          </div>
        </div>
      </div>

      {/* Designs */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8">
        <h2 className="font-display font-semibold text-xl text-navy mb-1">Elige un diseño</h2>
        <p className="text-sm text-nardo-500 mb-4">10 formatos disponibles. El color de cada uno se puede cambiar abajo.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pdf-designs-grid">
          {PDF_DESIGNS.map((d) => (
            <button key={d.id} type="button" onClick={() => setDesign(d.id)} data-testid={`pdf-design-${d.id}`}
              className={`text-left rounded-lg border-2 transition-all overflow-hidden ${design === d.id ? "border-navy ring-2 ring-navy/20" : "border-nardo-200 hover:border-navy/40"}`}>
              <div className="bg-nardo-50 p-1">
                <DesignPreview design={d.id} color={color} heading={heading || "Colegio"} logoUrl={previewLogo} scale={1} mini />
              </div>
              <div className={`text-xs font-medium px-2 py-1.5 ${design === d.id ? "bg-navy text-white" : "text-nardo-600"}`}>{d.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Color */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8">
        <h2 className="font-display font-semibold text-xl text-navy mb-1">Color del diseño</h2>
        <p className="text-sm text-nardo-500 mb-4">Cambia el color principal de encabezados y tablas.</p>
        <div className="flex flex-wrap items-center gap-3">
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="pdf-color-input"
            className="w-14 h-10 rounded-md border border-nardo-200 cursor-pointer bg-white" />
          <input type="text" value={color} onChange={(e) => setColor(e.target.value)} data-testid="pdf-color-hex"
            className="w-28 border border-nardo-200 rounded-md px-3 py-2 text-sm font-mono" />
          <div className="flex flex-wrap gap-2">
            {SWATCHES.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)} data-testid={`pdf-swatch-${c.replace('#','')}`}
                style={{ background: c }} className={`w-8 h-8 rounded-full border-2 ${color.toLowerCase() === c.toLowerCase() ? "border-navy scale-110" : "border-white shadow"}`} />
            ))}
          </div>
        </div>
      </div>

      {/* School name */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8">
        <h2 className="font-display font-semibold text-xl text-navy mb-1">Nombre en los PDF</h2>
        <p className="text-sm text-nardo-500 mb-4">Texto que aparece como título en los documentos. Por defecto, el nombre de tu colegio.</p>
        <input type="text" value={heading} onChange={(e) => setHeading(e.target.value)} maxLength={80}
          placeholder={user?.school_name || "Nombre del colegio"} data-testid="pdf-heading-input"
          className="w-full max-w-md border border-nardo-200 rounded-md px-3 py-2.5 text-sm" />
      </div>

      {/* Planeaciones */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8" data-testid="pdf-planeacion-card">
        <h2 className="font-display font-semibold text-xl text-navy mb-1">Membrete de planeaciones</h2>
        <p className="text-sm text-nardo-500 mb-4">Subtítulo que aparece bajo el nombre del colegio en las planeaciones (formato preescolar y NEM). Déjalo vacío para usar el texto por defecto.</p>
        <input type="text" value={planeacionSubtitle} onChange={(e) => setPlaneacionSubtitle(e.target.value)} maxLength={120}
          placeholder="Planeación Didáctica · Educación Inicial y Preescolar" data-testid="pdf-planeacion-subtitle-input"
          className="w-full max-w-lg border border-nardo-200 rounded-md px-3 py-2.5 text-sm" />
        <p className="text-[11px] text-nardo-400 mt-2">Ejemplos: “Planeación Didáctica · Ciclo 2025-2026”, “Plan de Trabajo Semanal”.</p>
      </div>

      {/* Logo */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8" data-testid="pdf-logo-card">
        <h2 className="font-display font-semibold text-xl text-navy mb-1">Logo para PDF</h2>
        <p className="text-sm text-nardo-500 mb-4">Logo dedicado a los documentos (independiente del logo del panel). Si no subes uno, se usa el logo del colegio.</p>
        <div className="rounded-lg overflow-hidden border border-nardo-200 bg-nardo-100 h-28 flex items-center justify-center mb-4" data-testid="pdf-logo-preview">
          {previewLogo ? <img src={previewLogo} alt="Logo PDF" className="h-20 w-auto max-w-[220px] object-contain" /> : <span className="text-sm text-nardo-400">Sin logo dedicado (se usa el logo del colegio)</span>}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input ref={logoRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
          <button type="button" onClick={() => logoRef.current?.click()} disabled={uploading} data-testid="pdf-logo-upload"
            className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50">
            <Upload className="w-4 h-4" /> {uploading ? "Subiendo…" : (previewLogo ? "Cambiar logo" : "Subir logo")}
          </button>
          {previewLogo && (
            <button type="button" onClick={removeLogo} data-testid="pdf-logo-delete"
              className="inline-flex items-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 px-4 py-2 rounded-md text-sm font-medium transition-colors">
              <Trash2 className="w-4 h-4" /> Quitar
            </button>
          )}
        </div>
        <div className="mt-5">
          <label className="text-sm font-medium text-navy">Tamaño del logo: <span className="font-mono">{Number(logoScale).toFixed(1)}×</span></label>
          <input type="range" min="0.6" max="2.6" step="0.1" value={logoScale} onChange={(e) => setLogoScale(parseFloat(e.target.value))}
            data-testid="pdf-logo-scale" className="w-full max-w-md accent-navy mt-2" />
          <div className="flex justify-between max-w-md text-[11px] text-nardo-400"><span>Pequeño</span><span>Grande</span></div>
        </div>
      </div>

      {/* Marca de agua */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8" data-testid="pdf-watermark-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display font-semibold text-xl text-navy mb-1">Marca de agua</h2>
            <p className="text-sm text-nardo-500">Muestra tu logo como fondo tenue en cada página del documento.</p>
          </div>
          <button type="button" role="switch" aria-checked={watermark} onClick={() => setWatermark((v) => !v)} data-testid="pdf-watermark-toggle"
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${watermark ? "bg-navy" : "bg-nardo-300"}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${watermark ? "translate-x-5" : ""}`} />
          </button>
        </div>
        {watermark && !previewLogo && <p className="text-xs text-amber-600 mt-3">Sube un logo de PDF arriba para que se vea la marca de agua (si no, se usa el logo del colegio).</p>}
      </div>

      {/* Firma y sello */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8" data-testid="pdf-signature-card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="font-display font-semibold text-xl text-navy mb-1">Firma y sello</h2>
            <p className="text-sm text-nardo-500">Aparecen al pie del documento (boletas, constancias, etc.).</p>
          </div>
          <button type="button" role="switch" aria-checked={showSignature} onClick={() => setShowSignature((v) => !v)} data-testid="pdf-signature-toggle"
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${showSignature ? "bg-navy" : "bg-nardo-300"}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${showSignature ? "translate-x-5" : ""}`} />
          </button>
        </div>
        {showSignature && (
          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium text-navy">Cargo bajo la firma</label>
              <input type="text" value={signatureLabel} onChange={(e) => setSignatureLabel(e.target.value)} maxLength={40}
                placeholder="Dirección" data-testid="pdf-signature-label"
                className="block w-full max-w-xs border border-nardo-200 rounded-md px-3 py-2 text-sm mt-1" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                { kind: "signature", title: "Firma", url: previewSig, ref: sigRef },
                { kind: "stamp", title: "Sello", url: previewStamp, ref: stampRef },
              ].map(({ kind, title, url, ref }) => (
                <div key={kind} data-testid={`pdf-${kind}-block`}>
                  <div className="text-sm font-medium text-navy mb-2">{title}</div>
                  <div className="rounded-lg overflow-hidden border border-nardo-200 bg-nardo-100 h-24 flex items-center justify-center mb-3">
                    {url ? <img src={url} alt={title} className="h-16 w-auto max-w-[160px] object-contain" /> : <span className="text-xs text-nardo-400">Sin {title.toLowerCase()}</span>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <input ref={ref} type="file" accept="image/*" onChange={(e) => uploadAsset(kind, e)} className="hidden" />
                    <button type="button" onClick={() => ref.current?.click()} disabled={assetUploading === kind} data-testid={`pdf-${kind}-upload`}
                      className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50">
                      <Upload className="w-4 h-4" /> {assetUploading === kind ? "Subiendo…" : (url ? "Cambiar" : "Subir")}
                    </button>
                    {url && (
                      <button type="button" onClick={() => removeAsset(kind)} data-testid={`pdf-${kind}-delete`}
                        className="inline-flex items-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md text-sm font-medium transition-colors">
                        <Trash2 className="w-4 h-4" /> Quitar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pie de página */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8" data-testid="pdf-footer-card">
        <h2 className="font-display font-semibold text-xl text-navy mb-1">Pie de página</h2>
        <p className="text-sm text-nardo-500 mb-4">Texto que aparece en la parte inferior de cada página (dirección, teléfono, redes).</p>
        <textarea value={footer} onChange={(e) => setFooter(e.target.value)} maxLength={200} rows={2}
          placeholder="Av. Reforma 123, CDMX · Tel. 55 1234 5678 · www.micolegio.mx" data-testid="pdf-footer-input"
          className="w-full border border-nardo-200 rounded-md px-3 py-2.5 text-sm resize-none" />
        <div className="text-[11px] text-nardo-400 mt-1">{footer.length}/200</div>
      </div>
    </div>
  );
}
